import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { RotateCwIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Spinner } from '@/components/spinner/spinner';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import {
  GET_NOTIFICATION_DELIVERY_QUERY_KEY,
  getNotificationDelivery,
} from '@/features/notification-deliveries/api/get-notification-delivery.api';
import { GET_NOTIFICATION_DELIVERIES_QUERY_KEY } from '@/features/notification-deliveries/api/get-notification-deliveries.api';
import { retryNotificationDelivery } from '@/features/notification-deliveries/api/retry-notification-delivery.api';
import {
  CHANNEL_LABELS,
  getLabel,
  NOTIFICATION_TYPE_LABELS,
  SECRET_BEARING_CONTENT_NOTE,
  SKIPPED_NOTE,
} from '@/features/notification-deliveries/constants/notification-delivery-labels.constant';
import type {
  DeliveryAttemptDTO,
  NotificationDeliveryDetailDTO,
} from '@/features/notification-deliveries/schemas/get-notification-delivery.schema';
import { getAttemptTriggerLabel } from '@/features/notification-deliveries/utils/get-attempt-trigger-label';
import { getRecipientDisplay } from '@/features/notification-deliveries/utils/get-recipient-display';
import { PERMISSIONS } from '@/features/roles/constants/permissions.constant';
import { useHasPermission } from '@/features/roles/hooks/use-has-permission';
import { formatDate } from '@/utils/date';

import { DeliveryStatusBadge } from '../delivery-status-badge/delivery-status-badge';

export interface DetailNotificationDeliveryDialogProps extends BaseDialogProps {
  deliveryId: string;
}

export default function DetailNotificationDeliveryDialog({
  dialog,
  deliveryId,
}: DetailNotificationDeliveryDialogProps) {
  const { closeDetailNotificationDeliveryDialog } = useV2DialogContext();
  const queryClient = useQueryClient();
  const hasRetryPermission = useHasPermission(
    PERMISSIONS.NOTIFICATION_DELIVERY_RETRY
  );

  const detailQueryKey = [GET_NOTIFICATION_DELIVERY_QUERY_KEY, deliveryId];

  const getNotificationDeliveryQuery = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => getNotificationDelivery(deliveryId),
  });

  const retryMutation = useMutation({
    mutationFn: () => retryNotificationDelivery(deliveryId),
    // Every call is a real send; never repeat it automatically.
    retry: false,
    onSuccess: updated => {
      queryClient.setQueryData(detailQueryKey, updated);
      queryClient.invalidateQueries({
        queryKey: [GET_NOTIFICATION_DELIVERIES_QUERY_KEY],
      });

      // HTTP 200 only means the retry ran; the send itself may have failed.
      if (updated.status === 'failed') {
        const error = updated.attempts[0]?.error ?? updated.lastError ?? '-';
        toast.error(`Pengiriman ulang gagal: ${error}`);
        return;
      }
      toast.success('Notifikasi berhasil dikirim ulang');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof AxiosError
          ? (error.response?.data?.message ?? error.message)
          : 'Gagal mengirim ulang notifikasi';
      toast.error(message);
      // 409 and 404 mean the detail changed under us.
      queryClient.invalidateQueries({ queryKey: detailQueryKey });
    },
  });

  const delivery = getNotificationDeliveryQuery.data;
  const canRetry = Boolean(delivery?.canRetry) && hasRetryPermission;
  const isRetrying = retryMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isRetrying) {
          closeDetailNotificationDeliveryDialog();
        }
      }}
    >
      <DialogContent className="sm:max-w-180">
        <DialogHeader>
          <DialogTitle>Detail Pengiriman Notifikasi</DialogTitle>
          <DialogDescription>
            Konten yang dikirim dan riwayat percobaan pengiriman.
          </DialogDescription>
        </DialogHeader>

        {getNotificationDeliveryQuery.isPending ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
            <Spinner className="size-8" />
            <span>Loading detail pengiriman.</span>
          </div>
        ) : getNotificationDeliveryQuery.isError || !delivery ? (
          <p className="py-10 text-center font-medium text-destructive">
            Gagal memuat detail pengiriman.
          </p>
        ) : (
          <DialogBody className="max-h-[65vh] gap-6">
            <DetailDeliveryHeader delivery={delivery} />
            <DetailDeliveryInfo delivery={delivery} />
            <DetailDeliveryContent delivery={delivery} />
            <DetailDeliveryAttempts attempts={delivery.attempts} />
          </DialogBody>
        )}

        {canRetry && (
          <DialogFooter>
            <Button
              disabled={isRetrying}
              onClick={() => retryMutation.mutate()}
            >
              <RotateCwIcon className="size-4" />
              {isRetrying ? 'Mengirim ulang...' : 'Retry'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DetailDeliverySectionProps {
  delivery: NotificationDeliveryDetailDTO;
}

function DetailDeliveryHeader({ delivery }: DetailDeliverySectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-4">
        <p className="text-lg font-semibold text-foreground">
          {getLabel(NOTIFICATION_TYPE_LABELS, delivery.notificationType)}
        </p>
        <DeliveryStatusBadge status={delivery.status} />
      </div>
      {delivery.status === 'skipped' && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-muted-foreground">
          {SKIPPED_NOTE}
        </p>
      )}
    </div>
  );
}

function DetailDeliveryInfo({ delivery }: DetailDeliverySectionProps) {
  const recipient = getRecipientDisplay(delivery.recipient);

  return (
    <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
      <InfoRow
        label="Penerima"
        value={
          recipient.kindLabel
            ? `${recipient.name} (${recipient.kindLabel})`
            : recipient.name
        }
      />
      <InfoRow
        label="Channel"
        value={getLabel(CHANNEL_LABELS, delivery.channel)}
      />
      <InfoRow label="Dibuat Pada" value={formatDate(delivery.createdAt)} />
      <InfoRow label="Alamat Terakhir" value={delivery.lastAddress ?? '-'} />
      {delivery.lastError && (
        <InfoRow label="Error Terakhir" value={delivery.lastError} />
      )}
    </div>
  );
}

function DetailDeliveryContent({ delivery }: DetailDeliverySectionProps) {
  const content = delivery.content;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">Konten</p>
      {delivery.isSecretBearing ? (
        <p className="text-muted-foreground">{SECRET_BEARING_CONTENT_NOTE}</p>
      ) : !content ? (
        <p className="text-muted-foreground">Konten tidak tersedia.</p>
      ) : content.type === 'email' ? (
        <div className="flex flex-col gap-2">
          <InfoRow label="Subjek" value={content.subject} />
          {/* Raw HTML as sent: an empty sandbox blocks scripts, forms and same-origin access. */}
          <iframe
            title="Konten email"
            sandbox=""
            srcDoc={content.html}
            className="h-96 w-full rounded-md border border-border bg-white"
          />
        </div>
      ) : content.type === 'whatsapp' ? (
        <p className="whitespace-pre-wrap wrap-break-word rounded-md border border-border p-3">
          {content.text}
        </p>
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
          <InfoRow label="Judul" value={content.title ?? '-'} />
          <InfoRow label="Isi" value={content.body ?? '-'} />
          <InfoRow label="URL Aksi" value={content.actionUrl ?? '-'} />
        </div>
      )}
    </div>
  );
}

function DetailDeliveryAttempts({
  attempts,
}: {
  attempts: DeliveryAttemptDTO[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">
        Riwayat Percobaan
      </p>
      {attempts.length === 0 ? (
        <p className="text-muted-foreground">Belum ada percobaan.</p>
      ) : (
        // Newest first, as ordered by the API.
        <ul className="flex flex-col gap-2">
          {attempts.map(attempt => (
            <li
              key={attempt.id}
              className="flex flex-col gap-2 rounded-md border border-border p-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                  <p className="font-medium text-foreground">
                    {getAttemptTriggerLabel(attempt)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(attempt.startedAt)}
                  </p>
                </div>
                <DeliveryStatusBadge status={attempt.result} />
              </div>
              <InfoRow label="Alamat" value={attempt.address ?? '-'} />
              {attempt.error && (
                <p className="wrap-break-word text-destructive">
                  {attempt.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="wrap-break-word text-sm font-medium text-foreground">
        {value}
      </p>
    </div>
  );
}
